import type { Component } from '../router';
import { HEALTH_EVENT, checkHealth, getLastHealth, type HealthState } from '../api/client';

const LABEL: Record<HealthState['kind'], string> = {
  unknown: 'Checking…',
  ok: 'Online',
  degraded: 'Degraded',
  error: 'Offline',
};

function detailText(state: HealthState): string {
  if (state.kind === 'ok' || state.kind === 'degraded') {
    return `Couchbase ${state.health.couchbase.status} · ${state.health.couchbase.latencyMs} ms`;
  }
  return LABEL[state.kind];
}

// Cross-cutting system-health indicator. Reads shared state from the API
// client and reflects live updates via the HEALTH_EVENT bus; the app shell
// mounts one instance per viewport (desktop top nav + phone chip).
export class HealthPill implements Component {
  private el: HTMLElement | null = null;
  private label: HTMLElement | null = null;

  private readonly onChange = (e: Event): void => {
    this.render((e as CustomEvent<HealthState>).detail);
  };

  mount(host: HTMLElement): void {
    const el = document.createElement('span');
    el.className = 'healthpill';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');

    const dot = document.createElement('span');
    dot.className = 'healthpill__dot';
    dot.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'healthpill__label';

    el.append(dot, label);
    host.appendChild(el);

    this.el = el;
    this.label = label;
    this.render(getLastHealth());

    window.addEventListener(HEALTH_EVENT, this.onChange);
    void checkHealth();
  }

  unmount(): void {
    window.removeEventListener(HEALTH_EVENT, this.onChange);
    this.el?.remove();
    this.el = null;
    this.label = null;
  }

  private render(state: HealthState): void {
    if (!this.el || !this.label) return;
    this.el.dataset.state = state.kind;
    this.label.textContent = LABEL[state.kind];
    const detail = detailText(state);
    this.el.setAttribute('aria-label', `System status: ${LABEL[state.kind]}. ${detail}`);
    this.el.title = detail;
  }
}
